import { useState, useMemo, useRef, useCallback } from "react";
import type { SignatureToken } from "types/signatureToken";

type Period = "3d" | "7d" | "30d" | "90d" | "12m";

const PERIOD_LABELS: Record<Period, string> = {
  "3d": "3 jours",
  "7d": "7 jours",
  "30d": "30 jours",
  "90d": "90 jours",
  "12m": "12 mois",
};

function groupSignatures(tokens: SignatureToken[], period: Period) {
  const now = new Date();

  if (period === "12m") {
    const buckets: { label: string; tooltipDate: string; count: number }[] = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const label = d.toLocaleDateString("fr-FR", { month: "short" }).toUpperCase().replace(".", "");
      const tooltipDate = d.toLocaleDateString("fr-FR", { month: "short", year: "numeric" });
      buckets.push({ label, tooltipDate, count: 0 });
      for (const t of tokens) {
        const tDate = new Date(t.created_at);
        if (tDate.getFullYear() === d.getFullYear() && tDate.getMonth() === d.getMonth()) {
          buckets[buckets.length - 1].count += 1;
        }
      }
    }
    return buckets;
  }

  const days = period === "3d" ? 3 : period === "7d" ? 7 : period === "30d" ? 30 : 90;
  const buckets: { label: string; tooltipDate: string; count: number }[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const showLabel = days <= 7 || i === days - 1 || i === 0 || i % Math.ceil(days / 6) === 0;
    const label = showLabel ? d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }).replace(".", "") : "";
    const tooltipDate = d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }).replace(".", "");
    buckets.push({ label, tooltipDate, count: 0 });
    for (const t of tokens) {
      if (t.created_at?.slice(0, 10) === key) {
        buckets[buckets.length - 1].count += 1;
      }
    }
  }
  return buckets;
}

export function SignaturesChart({
  tokens,
  showPeriodSelector = false,
  defaultPeriod = "3d",
}: {
  tokens: SignatureToken[];
  showPeriodSelector?: boolean;
  defaultPeriod?: Period;
}) {
  const [period, setPeriod] = useState<Period>(defaultPeriod);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const buckets = useMemo(() => groupSignatures(tokens, period), [tokens, period]);

  const totalCount = buckets.reduce((s, b) => s + b.count, 0);
  const values = buckets.map((b) => b.count);
  const max = Math.max(...values, 1);

  const chartW = 580;
  const chartH = 180;

  const points = values.map((v, i) => ({
    x: values.length > 1 ? (i / (values.length - 1)) * chartW : chartW / 2,
    y: chartH - (v / max) * (chartH - 20),
  }));

  const pathD = points.reduce((acc, p, i) => {
    if (i === 0) return `M ${p.x} ${p.y}`;
    const prev = points[i - 1];
    const cpx = (prev.x + p.x) / 2;
    return acc + ` C ${cpx} ${prev.y}, ${cpx} ${p.y}, ${p.x} ${p.y}`;
  }, "");

  const fillD = points.length > 0
    ? `${pathD} L ${points[points.length - 1].x} ${chartH} L ${points[0].x} ${chartH} Z`
    : "";

  const ySteps = 5;
  const yLabels = Array.from({ length: ySteps + 1 }, (_, i) => Math.round((max / ySteps) * i));

  const labeledBuckets = buckets.map((b, i) => ({ ...b, i })).filter((b) => b.label);

  const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg || points.length === 0) return;
    const rect = svg.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width) * (chartW + 40);
    let nearest = 0;
    let minDist = Infinity;
    points.forEach((p, i) => {
      const d = Math.abs(p.x - svgX);
      if (d < minDist) { minDist = d; nearest = i; }
    });
    setHoveredIdx(nearest);
  }, [points, chartW]);

  const handleMouseLeave = useCallback(() => setHoveredIdx(null), []);

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex flex-row justify-between items-center">
        <div className="flex flex-col">
          <div className="font-semibold text-black">Signatures envoyees</div>
          <div className="text-hx font-semibold text-indigo-500">
            {totalCount} signature{totalCount !== 1 ? "s" : ""}
          </div>
        </div>
        <div className="flex flex-row items-center gap-3">
          {showPeriodSelector && (
            <div className="flex flex-row items-center gap-1 bg-neutral-100 rounded-lg p-0.5">
              {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  className="text-h4 font-medium px-2.5 py-1 rounded-md transition-colors"
                  style={{
                    background: period === p ? "#FFFFFF" : "transparent",
                    color: period === p ? "#111827" : "#9CA3AF",
                    boxShadow: period === p ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
                  }}
                >
                  {PERIOD_LABELS[p]}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Chart */}
      {totalCount === 0 ? (
        <div className="flex items-center justify-center h-[180px] text-h3 text-neutral-300">
          Aucune signature sur cette periode
        </div>
      ) : (
        <div className="w-full pt-[50px]">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${chartW + 40} ${chartH + 30}`}
            width="100%"
            className="overflow-visible"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{ cursor: "crosshair" }}
          >
            <defs>
              <linearGradient id="sigChartFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#6366F1" stopOpacity="0.15" />
                <stop offset="100%" stopColor="#6366F1" stopOpacity="0.01" />
              </linearGradient>
            </defs>
            {/* Grid lines */}
            {yLabels.map((label) => {
              const y = chartH - (label / max) * (chartH - 20);
              return (
                <g key={label}>
                  <line x1={0} y1={y} x2={chartW} y2={y} stroke="#f0f0f0" strokeWidth={1} />
                  <text x={chartW + 8} y={y + 4} fontSize={11} fill="#a3a3a3">{label}</text>
                </g>
              );
            })}
            {/* Fill */}
            {fillD && <path d={fillD} fill="url(#sigChartFill)" />}
            {/* Line */}
            {pathD && <path d={pathD} fill="none" stroke="#6366F1" strokeWidth={2.5} />}
            {/* Hover tooltip */}
            {hoveredIdx !== null && points[hoveredIdx] && (
              <>
                <line
                  x1={points[hoveredIdx].x} y1={0}
                  x2={points[hoveredIdx].x} y2={chartH}
                  stroke="#e5e7eb" strokeWidth={1} strokeDasharray="4 2"
                />
                <circle cx={points[hoveredIdx].x} cy={points[hoveredIdx].y} r={5} fill="#6366F1" stroke="white" strokeWidth={2} />
                <g transform={`translate(${Math.min(Math.max(points[hoveredIdx].x - 55, 0), chartW - 110)}, ${points[hoveredIdx].y < 60 ? points[hoveredIdx].y + 15 : points[hoveredIdx].y - 50})`}>
                  <rect width={110} height={40} rx={8} fill="#1a1a1a" />
                  <text x={55} y={16} textAnchor="middle" fontSize={11} fill="#c8c8c8">
                    {buckets[hoveredIdx].tooltipDate}
                  </text>
                  <text x={55} y={32} textAnchor="middle" fontSize={13} fill="white" fontWeight="600">
                    {buckets[hoveredIdx].count} signature{buckets[hoveredIdx].count !== 1 ? "s" : ""}
                  </text>
                </g>
              </>
            )}
            {/* X labels */}
            {labeledBuckets.map((b) => (
              <text
                key={b.i}
                x={values.length > 1 ? (b.i / (values.length - 1)) * chartW : chartW / 2}
                y={chartH + 20}
                textAnchor="middle"
                fontSize={11}
                fill="#a3a3a3"
              >
                {b.label}
              </text>
            ))}
          </svg>
        </div>
      )}
    </div>
  );
}

export function SignatureSuccessRateChart({ tokens }: { tokens: SignatureToken[] }) {
  const stats = useMemo(() => {
    const signed = tokens.filter((t) => t.status === "signed").length;
    const pending = tokens.filter((t) => t.status === "pending").length;
    const expired = tokens.filter((t) => t.status === "expired").length;
    const superseded = tokens.filter((t) => t.status === "superseded").length;
    const total = tokens.length;
    const rate = total > 0 ? Math.round((signed / total) * 100) : 0;
    return { signed, pending, expired, superseded, total, rate };
  }, [tokens]);

  const size = 120;
  const strokeW = 14;
  const r = (size - strokeW) / 2;
  const circumference = 2 * Math.PI * r;
  const signedArc = stats.total > 0 ? (stats.signed / stats.total) * circumference : 0;
  const pendingArc = stats.total > 0 ? (stats.pending / stats.total) * circumference : 0;
  const expiredArc = stats.total > 0 ? (stats.expired / stats.total) * circumference : 0;

  const rateColor = "#6366F1";

  return (
    <div className="flex flex-col gap-3">
      <div className="font-semibold text-black">Taux de signature</div>
      {stats.total === 0 ? (
        <div className="flex items-center justify-center h-[120px] text-h3 text-neutral-300">
          Aucune demande de signature
        </div>
      ) : (
        <div className="flex flex-row items-center gap-6">
          {/* Donut */}
          <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
            <svg width={size} height={size} className="overflow-visible" style={{ transform: "rotate(-90deg)" }}>
              {/* Background */}
              <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#F5F5F5" strokeWidth={strokeW} />
              {/* Signed — solid indigo */}
              <circle
                cx={size / 2} cy={size / 2} r={r}
                fill="none" stroke="#6366F1" strokeWidth={strokeW}
                strokeDasharray={`${signedArc} ${circumference}`}
                strokeDashoffset={0}
                strokeLinecap="round"
              />
              {/* Pending — amber */}
              <circle
                cx={size / 2} cy={size / 2} r={r}
                fill="none" stroke="#F59E0B30" strokeWidth={strokeW}
                strokeDasharray={`${pendingArc} ${circumference}`}
                strokeDashoffset={-signedArc}
                strokeLinecap="round"
              />
              {/* Expired — rose */}
              <circle
                cx={size / 2} cy={size / 2} r={r}
                fill="none" stroke="#F43F5E15" strokeWidth={strokeW}
                strokeDasharray={`${expiredArc} ${circumference}`}
                strokeDashoffset={-(signedArc + pendingArc)}
                strokeLinecap="round"
              />
            </svg>
            {/* Center label */}
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xl font-bold" style={{ color: rateColor }}>{stats.rate}%</span>
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-col gap-2">
            <div className="flex flex-row items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ background: "#6366F1" }} />
              <span className="text-h3" style={{ color: "#6366F1" }}>Signees -- {stats.signed}</span>
            </div>
            <div className="flex flex-row items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ background: "#F59E0B30" }} />
              <span className="text-h3" style={{ color: "#D97706" }}>En attente -- {stats.pending}</span>
            </div>
            <div className="flex flex-row items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ background: "#F43F5E15" }} />
              <span className="text-h3" style={{ color: "#F43F5E" }}>Expirees -- {stats.expired}</span>
            </div>
            <div className="flex flex-row items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ background: "#9CA3AF30" }} />
              <span className="text-h3" style={{ color: "#6B7280" }}>Remplacees -- {stats.superseded}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
